/**
 * The lightbox by touch (docs/design/visual-overhaul.md §12.7 A6): two
 * fingers pinch the picture (1x to 4x; a double tap toggles 2.5x), one
 * finger drags it while it is zoomed, and at 1x a swipe sideways goes to the
 * next or the previous picture and a swipe down closes the viewer. The
 * picture follows the finger while it moves (its transform only, no
 * renders); a gesture too short to count springs back.
 *
 * A mouse does nothing here: the desktop keeps its arrows, keys and clicks.
 */
import { useEffect, useRef, type RefObject } from 'react';

export const LB_MIN_SCALE = 1;
export const LB_MAX_SCALE = 4;
/** A double tap zooms to this, or back to 1x. */
export const LB_TAP_SCALE = 2.5;
/** How far a finger must travel sideways to change the picture. */
export const LB_SWIPE_PX = 56;
/** How far down to close. */
export const LB_CLOSE_PX = 96;
const DOUBLE_TAP_MS = 300;
const TAP_SLOP = 10;

export type SwipeOutcome = 'next' | 'prev' | 'close' | null;

/**
 * What a one-finger gesture at 1x did: `dx`, `dy` its travel. Sideways
 * wins when it is mostly sideways; down closes when it is mostly down.
 * The UI is left to right: a swipe to the left shows the next picture.
 */
export function swipeOutcome(dx: number, dy: number, scale = 1): SwipeOutcome {
  if (scale > LB_MIN_SCALE + 0.01) return null;
  const ax = Math.abs(dx);
  const ay = Math.abs(dy);
  if (ax >= LB_SWIPE_PX && ax > ay * 1.5) return dx < 0 ? 'next' : 'prev';
  if (dy >= LB_CLOSE_PX && dy > ax * 1.5) return 'close';
  return null;
}

/** The zoom after a pinch from `startDist` to `dist` apart, from `startScale`. */
export function pinchScale(startScale: number, startDist: number, dist: number): number {
  if (startDist <= 0) return startScale;
  return Math.min(LB_MAX_SCALE, Math.max(LB_MIN_SCALE, (startScale * dist) / startDist));
}

/** Keeps a zoomed picture's offset inside what its zoom lets it travel. */
export function clampPan(
  x: number,
  y: number,
  scale: number,
  w: number,
  h: number,
): { x: number; y: number } {
  const mx = ((scale - 1) * w) / 2;
  const my = ((scale - 1) * h) / 2;
  return { x: Math.max(-mx, Math.min(mx, x)), y: Math.max(-my, Math.min(my, y)) };
}

interface Handlers {
  step(d: number): void;
  close(): void;
}

/**
 * Wires the gestures on the lightbox `layer` for the picture `img`. `key`
 * changes with the picture (it starts again at 1x).
 */
export function useLightboxTouch(
  layer: RefObject<HTMLElement | null>,
  img: RefObject<HTMLElement | null>,
  on: Handlers,
  key: unknown,
): void {
  const handlers = useRef(on);
  useEffect(() => {
    handlers.current = on;
  });
  useEffect(() => {
    const el = layer.current;
    if (!el) return;
    const pts = new Map<number, { x: number; y: number }>();
    let scale = 1;
    let pan = { x: 0, y: 0 };
    /** One finger: where it went down and the pan then. Two: the pinch's start. */
    let start: { x: number; y: number; pan: { x: number; y: number } } | null = null;
    let pinch: { dist: number; scale: number } | null = null;
    let lastTap = 0;
    let moved = false;
    const paint = (dx = 0, dy = 0, fade = 1, anim = false) => {
      const p = img.current;
      if (!p) return;
      p.style.transition = anim ? 'transform 160ms ease-out, opacity 160ms ease-out' : 'none';
      p.style.transform =
        scale > 1.01
          ? `translate(${pan.x}px, ${pan.y}px) scale(${scale})`
          : dx || dy
            ? `translate(${dx}px, ${dy}px)`
            : '';
      p.style.opacity = fade < 1 ? String(fade) : '';
    };
    const dist = () => {
      const [a, b] = [...pts.values()];
      return a && b ? Math.hypot(a.x - b.x, a.y - b.y) : 0;
    };
    const down = (e: PointerEvent) => {
      if (e.pointerType === 'mouse') return;
      // the close button, the arrows and "Open original" keep their taps
      if ((e.target as Element).closest('button, a')) return;
      pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pts.size === 1) {
        start = { x: e.clientX, y: e.clientY, pan: { ...pan } };
        moved = false;
      } else if (pts.size === 2) {
        pinch = { dist: dist(), scale };
        start = null;
        moved = true;
      }
    };
    const move = (e: PointerEvent) => {
      if (!pts.has(e.pointerId)) return;
      pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pinch && pts.size >= 2) {
        scale = pinchScale(pinch.scale, pinch.dist, dist());
        if (scale <= 1.01) pan = { x: 0, y: 0 };
        paint();
        return;
      }
      if (!start) return;
      const dx = e.clientX - start.x;
      const dy = e.clientY - start.y;
      if (Math.hypot(dx, dy) > TAP_SLOP) moved = true;
      if (scale > 1.01) {
        const p = img.current?.getBoundingClientRect();
        const w = p ? p.width / scale : innerWidth;
        const h = p ? p.height / scale : innerHeight;
        pan = clampPan(start.pan.x + dx, start.pan.y + dy, scale, w, h);
        paint();
        return;
      }
      // at 1x the picture follows the finger: sideways, or down (fading)
      if (Math.abs(dx) > Math.abs(dy)) paint(dx, 0);
      else if (dy > 0) paint(0, dy, Math.max(0.4, 1 - dy / 400));
    };
    const up = (e: PointerEvent) => {
      if (!pts.has(e.pointerId)) return;
      pts.delete(e.pointerId);
      if (pinch) {
        if (pts.size < 2) pinch = null;
        // the finger left behind starts a pan from here
        const rest = [...pts.values()][0];
        start = rest ? { x: rest.x, y: rest.y, pan: { ...pan } } : null;
        if (scale <= 1.01) {
          scale = 1;
          pan = { x: 0, y: 0 };
          paint(0, 0, 1, true);
        }
        return;
      }
      const s = start;
      start = null;
      if (!s) return;
      const dx = e.clientX - s.x;
      const dy = e.clientY - s.y;
      if (!moved) {
        // a double tap zooms in on that point, or back out
        const now = e.timeStamp;
        if (now - lastTap < DOUBLE_TAP_MS) {
          lastTap = 0;
          scale = scale > 1.01 ? 1 : LB_TAP_SCALE;
          pan = { x: 0, y: 0 };
          paint(0, 0, 1, true);
        } else lastTap = now;
        return;
      }
      if (scale > 1.01) return;
      const out = e.type === 'pointercancel' ? null : swipeOutcome(dx, dy, scale);
      if (out === 'close') handlers.current.close();
      else if (out) handlers.current.step(out === 'next' ? 1 : -1);
      else paint(0, 0, 1, true);
    };
    el.addEventListener('pointerdown', down);
    el.addEventListener('pointermove', move);
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', up);
    return () => {
      el.removeEventListener('pointerdown', down);
      el.removeEventListener('pointermove', move);
      el.removeEventListener('pointerup', up);
      el.removeEventListener('pointercancel', up);
      // each picture is its own <img> (keyed): nothing of its zoom carries over
    };
    // the picture (`key`) starts a fresh gesture at 1x
  }, [layer, img, key]);
}
